import { CurrencyIcon, ProCard, SearchIcon } from '@components'
import cn from '@integrations/classnames'
import { useMemo, useState } from '@integrations/react'
import { uiSize } from '@utils'
import { useCurrencies } from 'mmfintech-backend-api'
import type { AccountBalanceResponse, MerchantAccessLevel, Nullable } from 'mmfintech-backend-api/types'
import { formatMoney, tr } from 'mmfintech-commons'
import AccessLevelIcon from './AccessLevelIcon'
import styles from './AccountList.module.scss'

const SEARCH_THRESHOLD = 6

interface Props {
  accounts: AccountBalanceResponse[]
  selectedId?: number
  onSelect: (account: AccountBalanceResponse) => void
  /** Staff only: per-account access level shown next to the name. Omitted for owners/admins, who have full access. */
  getAccessLevel?: (accountId: number) => Nullable<MerchantAccessLevel>
}

/** The Accounts page's scan list: name, currency and balance only; details live in the workspace. */
function AccountList({ accounts, selectedId, onSelect, getAccessLevel }: Props) {
  const { getCurrencyPrecision } = useCurrencies()
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return accounts
    return accounts.filter((account) =>
      [account.name, account.currencyCode, String(account.id)].some((value) => value?.toLowerCase().includes(q))
    )
  }, [accounts, query])

  return (
    <ProCard className={cn('account-list')} data-test='account-list'>
      <div className={cn(styles['account-list__head'], 'account-list__head')}>
        <div className={cn(styles['account-list__title-row'], 'account-list__title-row')}>
          <h2 className={cn(styles['account-list__title'], 'account-list__title')}>
            {tr('FRONTEND.ACCOUNTS.LIST.TITLE', 'Your accounts')}
          </h2>
          <span className={cn(styles['account-list__count'], 'account-list__count')}>{accounts.length}</span>
        </div>

        {accounts.length > SEARCH_THRESHOLD && (
          <label className={cn(styles['account-list__search'], 'account-list__search')}>
            <SearchIcon />
            <input
              type='search'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tr('FRONTEND.ACCOUNTS.LIST.SEARCH', 'Search name, currency or ID')}
              aria-label={tr('FRONTEND.ACCOUNTS.LIST.SEARCH', 'Search name, currency or ID')}
              data-test='account-search'
            />
          </label>
        )}
      </div>

      <div className={cn(styles['account-list__scroll'], 'account-list__scroll')}>
        {filtered.map((account) => {
          const selected = account.id === selectedId
          return (
            <div key={account.id} data-test='table-row'>
              <button
                type='button'
                className={cn(styles['account-list__item'], 'account-list__item', {
                  [styles['selected']]: selected,
                  selected
                })}
                aria-pressed={selected}
                onClick={() => onSelect(account)}
                data-test='account-list-item'
              >
                <CurrencyIcon
                  currency={account.currencyCode}
                  size={uiSize(36)}
                  circle
                  className={cn(styles['account-list__icon'], 'account-list__icon')}
                />
                <span className={cn(styles['account-list__text'], 'account-list__text')} data-test='view-button'>
                  <span className={cn(styles['account-list__name-row'], 'account-list__name-row')}>
                    <span className={cn(styles['account-list__name'], 'account-list__name')} data-test='account-name'>
                      {account.name}
                    </span>
                    {getAccessLevel && <AccessLevelIcon level={getAccessLevel(account.id)} />}
                  </span>
                  <span
                    className={cn(styles['account-list__currency'], 'account-list__currency')}
                    data-test='view-button-mobile'
                  >
                    <span data-test='currency'>{account.currencyCode}</span>
                  </span>
                </span>
                <span className={cn(styles['account-list__balance'], 'account-list__balance')} data-test='balance'>
                  {formatMoney(account.balance, '', getCurrencyPrecision(account.currencyCode)).trim()}
                </span>
              </button>
            </div>
          )
        })}

        {filtered.length === 0 && (
          <div className={cn(styles['account-list__empty'], 'account-list__empty')}>
            {query
              ? tr('FRONTEND.ACCOUNTS.LIST.NO_MATCH', 'No accounts match “{{QUERY}}”.', { QUERY: query })
              : tr('FRONTEND.ACCOUNTS.LIST.EMPTY', 'No accounts yet.')}
          </div>
        )}
      </div>
    </ProCard>
  )
}

export default AccountList
